import { Divider, Stack } from '@mui/material';
import { PackageCalculation } from '@retail/my-assortment/types';
import { PriceSuggestionPrice } from '@retail/price-suggestion/types';
import {
  CalculusHeading,
  FreightConditionAccordion,
  MarkupConditionAccordion,
  PriceRoundingConditionAccordion,
  PriceSuggestionConditionAccordion,
  PurchaseConditionAccordion,
  SalesPriceConditionAccordion,
  SalesPriceSummary,
} from '@retail/my-assortment/components';
import {
  EditItemAgreementConditionDialog,
  EditSalesPriceConditionFromCalculusCardDialog,
} from '@retail/calculus/containers';
import { useDisclosure } from '@shared/hooks';
import { getLLDate } from '@retail/utils';
import { useMemo } from 'react';
import { useAppTFunction } from '@retail/app/i18n';
import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
} from '@retail/app/stores/selected-context';
import { LookupRoundingRuleDialog } from '@retail/rounding/containers';
import { UniversalFormProvider } from '@shared/components';
import { SalesPriceConditionSchema, salesPriceConditionSchema } from '@retail/calculus/components';

interface Props {
  assortmentId: number;
  packageSupplierItem: PackageCalculation;
  mgSupplierItemId: number;
  selectedDate: string;
  showPriceSuggestion: boolean;
  canEditMarkup: boolean;
  isSalesPriceConditionEnabled: boolean;
  availablePriceSuggestionItemPrices: PriceSuggestionPrice[];
  mgItemNumber: number;
  supplierNumber: number;
  primaryText: string;
  incVat: boolean;
}

export function PackageCalculusCalculations({
  assortmentId,
  packageSupplierItem,
  mgSupplierItemId,
  selectedDate,
  showPriceSuggestion,
  canEditMarkup,
  isSalesPriceConditionEnabled,
  availablePriceSuggestionItemPrices,
  mgItemNumber,
  supplierNumber,
  primaryText,
  incVat,
}: Props) {
  const t = useAppTFunction();
  const { userCanRead, userCanUpdate } = usePermissionsForCurrentUser();
  const isPriceRoundingEnabled =
    useIsFeatureEnabled('price-rounding') && userCanRead('PRICE_CALCULATION__ROUNDING');
  const canEditPurchaseCondition = userCanUpdate('PRICE_CALCULATION__PURCHASE_CONDITION');
  const canEditSalesPriceCondition = userCanUpdate('PRICE_CALCULATION__SALES_PRICE_CONDITION');
  const canEditPriceSuggestionCondition = userCanUpdate('PRICE_CALCULATION__PRICE_SUGGESTIONS');
  const {
    isOpen: editPurchaseConditionIsOpen,
    onOpen: openEditPurchaseConditionDialog,
    onClose: closeEditPurchaseConditionDialog,
  } = useDisclosure();
  const { onOpen: openEditPriceSuggestionPriorityDialog } = useDisclosure();
  const { onOpen: openCreateMarkupConditionDialog } = useDisclosure();
  const {
    isOpen: createSalesPriceConditionIsOpen,
    onOpen: openEditSalesPriceConditionDialog,
    onClose: closeEditSalesPriceConditionDialog,
  } = useDisclosure();

  const roundingRuleDialog = useDisclosure();

  const datedPrices = useMemo(
    () => packageSupplierItem.prices.find((item) => getLLDate(item.date) === selectedDate),
    [packageSupplierItem.prices, selectedDate]
  );

  const defaultSalesPriceEditValues: SalesPriceConditionSchema = useMemo(() => {
    const defaultValues = salesPriceConditionSchema(t).getDefault();
    return {
      ...defaultValues,
      markupCoverageDegree:
        datedPrices?.markupCondition?.bindingType === 'MG_SUPPLIER_ITEM' &&
        datedPrices.markupCondition.coverageDegree != null
          ? datedPrices.markupCondition.coverageDegree
          : defaultValues.markupCoverageDegree,
      priceSourcePriority:
        datedPrices?.priceSuggestionCondition?.bindingType === 'MG_SUPPLIER_ITEM'
          ? datedPrices.priceSuggestionCondition.priceSuggestionPriorities?.map(
              (prio) => prio.priceSuggestionType
            ) ?? []
          : defaultValues.priceSourcePriority,
    };
  }, [datedPrices, t]);

  return (
    <Stack>
      <CalculusHeading selectedUnit={packageSupplierItem.unit} />

      {datedPrices && (
        <Stack divider={<Divider />} gap={0.5} pt={0.5}>
          <PurchaseConditionAccordion
            purchaseCondition={datedPrices.purchaseCondition}
            purchasePrice={datedPrices.priceExVat?.purchasePrice}
            openEditDialog={canEditPurchaseCondition ? openEditPurchaseConditionDialog : undefined}
          />
          <FreightConditionAccordion
            freightCondition={datedPrices.freightCondition}
            costPrice={datedPrices.priceExVat?.costPrice}
          />
          {!isSalesPriceConditionEnabled ? (
            <Stack divider={<Divider />} gap={0.5}>
              <MarkupConditionAccordion
                selectedDatedPrices={datedPrices}
                consistsOfCount={packageSupplierItem.consistsOfCount}
                canEditMarkup={canEditMarkup}
                incVat={incVat}
                createMarkupCondition={openCreateMarkupConditionDialog}
              />
              {showPriceSuggestion && (
                <PriceSuggestionConditionAccordion
                  priceSuggestionCondition={datedPrices.priceSuggestionCondition}
                  priceSuggestionType={datedPrices.priceSuggestionType}
                  priceSuggestionPrice={
                    incVat
                      ? datedPrices.priceIncVat.priceSuggestionPrice
                      : datedPrices.priceExVat.priceSuggestionPrice
                  }
                  availablePriceSuggestionItemPrices={availablePriceSuggestionItemPrices}
                  editPriceSuggestionPriority={
                    canEditPriceSuggestionCondition
                      ? openEditPriceSuggestionPriorityDialog
                      : undefined
                  }
                />
              )}
            </Stack>
          ) : (
            <SalesPriceConditionAccordion
              activePriceSuggestionType={datedPrices.priceSuggestionType}
              priceSuggestionCondition={datedPrices.priceSuggestionCondition}
              markupCondition={datedPrices.markupCondition}
              priceSuggestionPrice={
                incVat
                  ? datedPrices.priceIncVat.priceSuggestionPrice
                  : datedPrices.priceExVat.priceSuggestionPrice
              }
              markupPrice={
                incVat ? datedPrices.priceIncVat?.markupPrice : datedPrices.priceExVat?.markupPrice
              }
              availablePriceSuggestions={availablePriceSuggestionItemPrices}
              editSalesPriceCondition={
                canEditSalesPriceCondition ? openEditSalesPriceConditionDialog : undefined
              }
            />
          )}

          {isPriceRoundingEnabled && (
            <PriceRoundingConditionAccordion
              roundingCondition={datedPrices.roundingCondition}
              price={
                incVat
                  ? datedPrices.priceIncVat?.roundedPrice
                  : datedPrices.priceExVat?.roundedPrice
              }
              openRoundingRuleDialog={roundingRuleDialog.onOpen}
            />
          )}

          <SalesPriceSummary
            salesPrice={
              incVat ? datedPrices.priceIncVat.salesPrice : datedPrices.priceExVat?.salesPrice
            }
            salesCoverage={datedPrices.actualCoverageDegree}
            packageUnit={packageSupplierItem.unit}
            incVat={incVat}
          />
        </Stack>
      )}

      {editPurchaseConditionIsOpen && (
        <EditItemAgreementConditionDialog
          open
          onClose={closeEditPurchaseConditionDialog}
          assortmentId={assortmentId}
          supplierItemId={mgSupplierItemId}
          primaryText={primaryText}
        />
      )}

      {createSalesPriceConditionIsOpen && (
        <UniversalFormProvider
          schema={salesPriceConditionSchema(t)}
          defaultValues={defaultSalesPriceEditValues}
        >
          <EditSalesPriceConditionFromCalculusCardDialog
            open
            itemName={primaryText}
            mgItemNumber={mgItemNumber}
            supplierNumber={supplierNumber}
            mgSupplierItemNumber={mgSupplierItemId}
            markupCondition={datedPrices?.markupCondition}
            priceSourceCondition={datedPrices?.priceSuggestionCondition}
            onClose={closeEditSalesPriceConditionDialog}
          />
        </UniversalFormProvider>
      )}
      {roundingRuleDialog.isOpen && datedPrices?.roundingCondition && (
        <LookupRoundingRuleDialog
          priceRoundingRuleId={datedPrices.roundingCondition.roundingRule.id}
          open
          onClose={roundingRuleDialog.onClose}
        />
      )}
    </Stack>
  );
}
